/** biome-ignore-all lint/correctness/useExhaustiveDependencies: <explanation> */
import { Label } from '@radix-ui/react-dropdown-menu';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { FiDownload } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { useRecoilState } from 'recoil';
import { useDebounce } from 'use-debounce';

import { queryClient } from '@/App';

import { Button } from '@/components/atoms/Button';
import Checkbox from '@/components/atoms/Checkbox';
import {
	Tooltip,
	TooltipArrow,
	TooltipContent,
	TooltipPortal,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/atoms/ToolTip';
import BasicTabs from '@/components/molecules/BasicTabs';
import { EmployeeByActivityPoleTab } from '@/components/molecules/EmployeeByActivityPoleTab';
import NewSideFilter from '@/components/molecules/NewSideFilter';
import {
	EmployeeByActivityPoleCountSchema,
	type EmployeeByActivityPoleFilterOptions,
	EmployeeByActivityPoleFilterOptionsSchema,
} from '@/constants/EmployeeByActivityPoleConstants';
import { filters } from '@/constants/filters/EmployeeByActivityPoleFilter';
import api from '@/services/apiSgft';
import { employeeByActivityPoleFilterAtom } from '@/state/EmployeeByActivityPole.atom';
import { downloadFile } from '@/utils/downloadFile';
import { getQueryParams } from '@/utils/EmployeeByActivityPole/get-employee-by-activity-pole-query-params.utils';
import {
	isAdmin,
	userOnlyCanSeeAllEmployeeByActivityPole,
} from '@/utils/handleSavePermissions';

export const EmployeeByActivityPole = () => {
	const [filteredData, setFilteredData] = useRecoilState(
		employeeByActivityPoleFilterAtom,
	);
	const [employeeSearch, setEmployeeSearch] = useState('');
	const [debouncedEmployeeSearch] = useDebounce(employeeSearch, 500);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isExportingEmployeByActivity, setIsExportingEmployeByActivity] =
		useState(false);
	const fetchEmployeeByActivityPoleFilters = async () => {
		try {
			const response = await api.get(
				'activity/employee-by-activity-pole/filters',
				{
					params: getQueryParams(filteredData),
				},
			);
			const parsedData = EmployeeByActivityPoleFilterOptionsSchema.parse(
				response.data,
			);
			return parsedData;
		} catch (e) {
			toast.error('Erro ao carregar os dados', {
				theme: 'colored',
				toastId: 'error',
			});
			throw e;
		}
	};
	const { data: filterOptions, isLoading: isLoadingFilters } =
		useQuery<EmployeeByActivityPoleFilterOptions>(
			['employee-by-activity-pole-filters', filteredData],
			fetchEmployeeByActivityPoleFilters,
		);

	const fetchEmployeeByActivityPoleCount = async () => {
		try {
			const queryParams = getQueryParams(filteredData);
			const response = await api.get(
				'activity/employee-by-activity-pole/count',
				{
					params: {
						...queryParams,
						employeeSearch: debouncedEmployeeSearch,
						showAllEmployees: filteredData.showAllEmployees,
					},
				},
			);
			const parsedData = EmployeeByActivityPoleCountSchema.parse(response.data);
			return parsedData;
		} catch (e) {
			toast.error('Erro ao carregar os dados', {
				theme: 'colored',
				toastId: 'error',
			});
			throw e;
		}
	};
	const { data: tabsCount } = useQuery<{
		allowed: number;
		atRiskOfBlocking: number;
		blocked: number;
	}>(
		['employee-by-activity-pole-count', filteredData, debouncedEmployeeSearch],
		fetchEmployeeByActivityPoleCount,
	);

	const handleExportEmployeeByActivity = async () => {
		setIsExportingEmployeByActivity(true);
		const queryParams = getQueryParams(filteredData);
		await downloadFile(
			'extraction/employee-by-activity',
			'colaboradores-por-atividade',
			undefined,
			{
				...queryParams,
				employeeSearch: debouncedEmployeeSearch,
			},
		);
		setIsExportingEmployeByActivity(false);
	};
	const isShowAllDisabled = userOnlyCanSeeAllEmployeeByActivityPole();
	useEffect(() => {
		if (isShowAllDisabled && !filteredData.showAllEmployees) {
			setFilteredData((prev) => ({ ...prev, showAllEmployees: true }));
		}
	}, [isShowAllDisabled]);

	const handleRunRoutine = async () => {
		setIsSubmitting(true);
		try {
			await api.post('activity/calculate-employee-by-activity-pole');
			toast.success('Bloqueios recalculadas com sucesso', {
				theme: 'colored',
				toastId: 'success',
			});
			queryClient.invalidateQueries({
				queryKey: ['employee-by-activity-pole-count'],
			});
			queryClient.invalidateQueries({
				queryKey: ['employee-by-activity-pole-filters'],
			});
			queryClient.invalidateQueries({
				queryKey: ['employee-by-activity-pole'],
			});
		} catch (e) {
			toast.error('Erro ao recalcular Bloqueios', {
				theme: 'colored',
				toastId: 'error',
			});
		} finally {
			setIsSubmitting(false);
		}
	};
	const userCanRunRoutine = isAdmin();
	return (
		<div className="relative flex h-[calc(100%-3rem)] max-h-screen w-full flex-col items-start">
			<div className="absolute -top-8 right-16 flex items-center">
				{userCanRunRoutine && (
					<Button
						onClick={handleRunRoutine}
						className="h-8 rounded-2xl bg-primary normal-case text-white hover:bg-[#4F46E5] w-40"
						disabled={isSubmitting}
						isLoading={isSubmitting}
					>
						<span>Recalcular Bloqueios</span>
					</Button>
				)}
				<div className="flex h-[2rem] w-6 items-center justify-between rounded-lg p-4 text-[13px]">
					<TooltipProvider>
						<Tooltip>
							<TooltipTrigger>
								<Button
									className="p-2 hover:opacity-90"
									disabled={isExportingEmployeByActivity}
									isLoading={isExportingEmployeByActivity}
									variant={'destructive'}
								>
									<FiDownload
										onClick={handleExportEmployeeByActivity}
										size={16}
									/>
								</Button>
							</TooltipTrigger>
							<TooltipPortal>
								<TooltipContent
									className="bg-white"
									arrowPadding={50}
									sideOffset={1}
								>
									<TooltipArrow className="h-3 w-6 fill-white" />
									<p>Extração de Executantes (Primários e Terceiros)</p>
								</TooltipContent>
							</TooltipPortal>
						</Tooltip>
					</TooltipProvider>
				</div>
			</div>
			<NewSideFilter
				filters={filters}
				atom={employeeByActivityPoleFilterAtom}
				options={filterOptions}
				isLoading={isLoadingFilters}
			>
				<div className="my-1 flex items-center gap-2">
					<Checkbox
						defaultChecked={false}
						onChange={() =>
							setFilteredData((prev) => ({
								...prev,
								showAllEmployees: !prev.showAllEmployees,
							}))
						}
						checked={!!filteredData.showAllEmployees}
						disabled={isShowAllDisabled}
					/>
					<Label className="text-xs font-normal text-white">
						Mostrar todos os Colaboradores
					</Label>
				</div>
			</NewSideFilter>
			<div className="flex h-full max-h-full w-full flex-col gap-4">
				<BasicTabs
					initialTab={0}
					background="transparent"
					tabsNames={[
						'Bloqueados para PTs',
						'Em risco de bloqueio para PTs',
						'Liberados',
					]}
					tabsCounts={[
						tabsCount?.blocked ?? 0,
						tabsCount?.atRiskOfBlocking ?? 0,
						tabsCount?.allowed ?? 0,
					]}
					tabs={[
						<div key={0} className="w-full min-w-min max-w-full  pt-2">
							<EmployeeByActivityPoleTab
								employeeSearch={employeeSearch}
								debouncedEmployeeSearch={debouncedEmployeeSearch}
								handleUpdateEmployeeSearch={setEmployeeSearch}
								allowed={false}
							/>
						</div>,
						<div key={1} className="w-full min-w-min max-w-full  pt-2">
							<EmployeeByActivityPoleTab
								employeeSearch={employeeSearch}
								debouncedEmployeeSearch={debouncedEmployeeSearch}
								handleUpdateEmployeeSearch={setEmployeeSearch}
								allowed={true}
								atRiskOfBlocking={true}
							/>
						</div>,
						<div key={2} className="w-full min-w-min max-w-full  pt-2">
							<EmployeeByActivityPoleTab
								employeeSearch={employeeSearch}
								debouncedEmployeeSearch={debouncedEmployeeSearch}
								handleUpdateEmployeeSearch={setEmployeeSearch}
								allowed={true}
							/>
						</div>,
					]}
				/>
			</div>
		</div>
	);
};
